import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/BgMaxPrice';
import { calculatePrice } from '~/BgMaxPrice/calculatePrice';
import { DebounceInput } from '~/Debounce';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import ProductStatusIcon from '~/ProductStatusIcon';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';

export interface ReferencePriceValue {
	parentValue: null;
	prompt: ReactNode;
	value: string;
}
export interface Values {
	packageSize: {
		isValid: boolean | null;
		value: string;
	};
	packageSizeForSingleUnit: {
		isValid: boolean | null;
		value: string;
	};
	referencePrice: {
		isValid: boolean;
		value: ReferencePriceValue | null;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;

export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	detailLink(product: { id: string }): { pathname: string; search: string };
	disableSelection: boolean;
	highlightProduct: boolean;
	bgMaxPriceProduct: Product;
	usedForCalculation: boolean;
	lastSelectedProduct: boolean;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	referencePackageSize: number | null;
	showReferenceDataSource: boolean;
	historicData: boolean;
	style: React.CSSProperties;
	values: Values;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	public static defaultProps = {
		disableSelection: false,
		highlightProduct: false,
		lastSelectedProduct: false,
		referencePackageSize: null,
		showReferenceDataSource: false,
		style: null,
	};

	private readonly resolveReferencePrice = memoizeOne(
		(type: string | null, bgMaxPriceProduct: Product): ReferencePrice | null =>
			type !== null
				? bgMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ??
				  null
				: null
	);

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			JSON.stringify(this.props.bgMaxPriceProduct) !== JSON.stringify(nextProps.bgMaxPriceProduct) ||
			this.props.usedForCalculation !== nextProps.usedForCalculation ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.showReferenceDataSource !== nextProps.showReferenceDataSource ||
			this.props.historicData !== nextProps.historicData ||
			this.props.style !== nextProps.style ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.checked !== nextProps.checked
		);
	}

	public render() {
		const {
			bgMaxPriceProduct,
			usedForCalculation,
			values,
			referencePackageSize,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			showReferenceDataSource,
			detailLink,
			historicData,
			style,
			checked,
			onChange,
			onSave,
			checkRow,
		} = this.props;
		const { product } = bgMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value || null, bgMaxPriceProduct);
		const showReferencePricesSelect =
			bgMaxPriceProduct.referencePrices.length > 1 ||
			(bgMaxPriceProduct.referencePrices.length > 0 && bgMaxPriceProduct.allowsEmptyCalculator.exFactory);
		const prompt =
			referencePrice !== null ? (
				this.renderReferencePricePrompt(referencePrice)
			) : showReferencePricesSelect ? (
				<div className={styles.noPriceOption}>No price</div>
			) : null;

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr
							className={styles.datasetSourceShortName}
							title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
						>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{bgMaxPriceProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) => {
									onChange(bgMaxPriceProduct, values.referencePrice.value, values.packageSize.value, value);
								}}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{showReferencePricesSelect ? (
						this.renderReferencePriceSelect(prompt)
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
					{bgMaxPriceProduct.singleUnitPackageSize ? (
						<Tooltip
							position='right'
							reference={
								<span className={styles.exclamationMarkIcon}>
									<Icon name='red-exclamation-mark'>!</Icon>
								</span>
							}
							width={180}
						>
							Single unit package size
						</Tooltip>
					) : null}
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.packageSize.value}
							onBlur={onSave}
							onChange={(value) => {
								onChange(bgMaxPriceProduct, values.referencePrice.value, value, values.packageSizeForSingleUnit.value);
							}}
						>
							<Input
								id={`product-package-for-calc-${product.id}`}
								isInvalid={values.packageSize.isValid === false}
								placeholder={String(referencePackageSize || '')}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.comparativePriceCell}>
					<Link
						data-testid='calculated-price'
						styles={{ button: styles.comparativePriceCellContent }}
						tabIndex={-1}
						to={detailLinkString}
					>
						{this.renderCalculatedPrice(referencePrice)}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ProductStatusIcon
						historicData={historicData}
						referenceDataSource={showReferenceDataSource ? bgMaxPriceProduct.referenceDataSource : undefined}
						status={product.status}
					/>
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
				<div className={styles.usedForCalculationCell}>
					<Link styles={{ button: styles.usedForCalculationCellContent }} tabIndex={-1} to={detailLinkString}>
						{usedForCalculation ? <Icon name='formula' title='Used for calculation' /> : null}
					</Link>
				</div>
			</div>
		);
	}

	private renderCalculatedPrice(referencePrice: ReferencePrice | null) {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}
		return calculatePrice(
			referencePrice.calculatedPrice.price,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	private renderReferencePricePrompt(price: ReferencePrice) {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
				</div>
			</abbr>
		);
	}

	private renderReferencePriceSelect(prompt: ReactNode) {
		const { bgMaxPriceProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) => {
					onChange(bgMaxPriceProduct, value, values.packageSize.value, values.packageSizeForSingleUnit.value);
				}}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						<SegmentedSelectOption value={null}>
							<div className={styles.noPriceOption}>No price</div>
						</SegmentedSelectOption>

						{bgMaxPriceProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.price.roundedHumanFormat()} Comparative price</div>
								<div>
									<strong>
										{price.sourcePrice.price?.roundedHumanFormat()} {price.sourcePrice.humanFormatType()}
									</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	private readonly selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		const { bgMaxPriceProduct, onSelect } = this.props;
		onSelect(bgMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};
}
